import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, RefreshCw, SendToBack } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { PanelHeader } from './_shared';
import { LedgerAccessLink } from './LedgerAccessLink';

const http = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL}/api`,
  withCredentials: true,
});

const money = (row) => {
  const cents = Number(row.amount_total_cents ?? row.amount ?? 0);
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: String(row.currency || 'usd').toUpperCase(),
  }).format(cents / 100);
};

export const StripePaymentsPanel = () => {
  const [data, setData] = useState({ rows: [], count: 0, unposted_paid_count: 0 });
  const [busy, setBusy] = useState(false);
  const [posting, setPosting] = useState(null);

  const reload = useCallback(async () => {
    setBusy(true);
    try {
      const response = await http.get('/admin/gl/stripe-payments');
      setData(response.data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not load Stripe payments.');
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const postToLedger = async (row) => {
    const id = row.session_id || row.id;
    setPosting(id);
    try {
      const response = await http.post(`/admin/gl/stripe-payments/${encodeURIComponent(id)}/post`);
      setData((current) => ({
        ...current,
        unposted_paid_count: Math.max(0, Number(current.unposted_paid_count || 0) - 1),
        rows: current.rows.map((item) => {
          const itemId = item.session_id || item.id;
          return itemId === id
            ? { ...item, je_id: response.data.je_id, ledger_status: 'posted' }
            : item;
        }),
      }));
      toast.success(`Posted to Ledger as ${response.data.je_id}.`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Could not post Stripe payment.');
    } finally {
      setPosting(null);
    }
  };

  return (
    <section className="space-y-4" data-testid="stripe-payments-panel">
      <PanelHeader
        icon={CreditCard}
        title="Stripe Payments"
        subtitle="Review payment transactions and post any paid item missing its double-entry journal record."
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" variant="outline" onClick={reload} disabled={busy} data-testid="stripe-payments-refresh">
          {busy ? <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-2 h-3.5 w-3.5" />}
          Refresh
        </Button>
        <LedgerAccessLink testId="stripe-payments-open-ledger" />
        <span className="ml-auto text-[11px] text-muted-foreground" data-testid="stripe-payments-summary">
          {data.count} transactions · {data.unposted_paid_count} paid awaiting Ledger
        </span>
      </div>
      <div className="overflow-x-auto rounded-md border border-border bg-card">
        <table className="w-full text-[11.5px]">
          <thead className="bg-panel-header/40 text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Payment</th>
              <th className="px-3 py-2 text-left">User / kind</th><th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Ledger</th><th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row) => {
              const id = row.session_id || row.id;
              return (
                <tr key={id} className="border-t border-border/60" data-testid={`stripe-payment-row-${id}`}>
                  <td className="whitespace-nowrap px-3 py-2 text-muted-foreground">{row.created_at ? new Date(row.created_at).toLocaleString() : '—'}</td>
                  <td className="max-w-56 truncate px-3 py-2 font-mono-stat">{id}</td>
                  <td className="px-3 py-2">{row.user_id || 'guest'} · {row.kind || row.wallet || 'checkout'}</td>
                  <td className="px-3 py-2 text-right font-mono-stat">{money(row)}</td>
                  <td className="px-3 py-2">
                    <span data-testid={`stripe-payment-ledger-status-${id}`} className={`rounded-sm px-2 py-0.5 text-[10px] font-semibold uppercase ${row.ledger_status === 'posted' ? 'bg-success/15 text-success' : row.ledger_status === 'ready' ? 'bg-warning/15 text-warning' : 'bg-muted text-muted-foreground'}`}>
                      {row.je_id || row.ledger_status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {row.ledger_status === 'ready' && (
                      <Button size="sm" onClick={() => postToLedger(row)} disabled={posting === id} data-testid={`stripe-payment-post-${id}`}>
                        {posting === id ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <SendToBack className="mr-1.5 h-3.5 w-3.5" />}
                        Post to Ledger
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {!data.rows.length && !busy && <tr><td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">No Stripe payment transactions yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </section>
  );
};